import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import Globe from "./Globe";
import { clamp, img } from "./utils";

// [latitude, longitude] the globe turns to for each step, in order.
// A step can set its own place with latitude / longitude fields; these are the fallback.
const DEFAULT_POINTS = [
  [16.95, 74.41], // Ashta
  [18.52, 73.86], // Pune
  [19.6, 75.3], // Maharashtra
  [21.15, 79.09], // India
  [48.78, 10.1], // ZEISS, Germany
  [17.68, 75.33], // Pandharpur
  [18.52, 73.86], // Pune
];

const pointFor = (s, i) => {
  if (s.latitude != null && s.longitude != null) {
    const lat = Number(s.latitude);
    const lng = Number(s.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  return DEFAULT_POINTS[i % DEFAULT_POINTS.length];
};

const pad = (v) => String(v).padStart(2, "0");

const JourneyStage = ({ steps, photos }) => {
  const n = steps.length;
  const wrap = useRef(null);
  const stage = useRef(null);
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  // 0 → 1 while the pinned stage travels through the tall wrapper
  const progress = useMotionValue(0);
  const headTop = useTransform(progress, (v) => `${((0.5 + v * (n - 1)) / n) * 100}%`);
  const points = useMemo(() => steps.map(pointFor), [steps]);

  useMotionValueEvent(progress, "change", (v) => {
    const i = Math.min(n - 1, Math.max(0, Math.floor(v * n)));
    setActive((p) => (p === i ? p : i));
  });

  useEffect(() => {
    let raf = 0;
    const update = () => {
      const w = wrap.current;
      const s = stage.current;
      if (!w || !s) return;
      const top = parseFloat(getComputedStyle(s).top) || 0;
      const travel = w.offsetHeight - s.offsetHeight;
      const y = top - w.getBoundingClientRect().top;
      progress.set(travel > 0 ? clamp(y / travel) : 0);
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [n, progress]);

  const goTo = (i) => {
    const w = wrap.current;
    const s = stage.current;
    if (!w || !s) return;
    const top = parseFloat(getComputedStyle(s).top) || 0;
    const travel = w.offsetHeight - s.offsetHeight;
    const wrapTop = w.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: wrapTop - top + travel * ((i + 0.5) / n),
      behavior: reduce ? "auto" : "smooth",
    });
  };

  const step = steps[active];
  const bg = step.image || photos[active % Math.max(photos.length, 1)]?.image || "";
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section id="journey" className="au-j" aria-label="Our journey">
      <div ref={wrap} style={{ height: `calc(var(--au-stage) + ${n * 70}vh)` }}>
        <div ref={stage} className="au-j__stage">
          {/* the picture of the active step fills the background */}
          <div className="au-j__bg" aria-hidden="true">
            <AnimatePresence initial={false}>
              <motion.div
                key={`${active}-${bg}`}
                className="au-j__bgimg"
                style={bg ? { backgroundImage: `url("${img(bg)}")` } : undefined}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 1.1, ease }}
              />
            </AnimatePresence>
            <div className="au-j__shade" />
          </div>

          {/* left: the information of the active step */}
          <div className="au-j__text">
            <span className="au-eyebrow au-eyebrow--light">Our journey · since 1995</span>
            <AnimatePresence mode="wait">
              <motion.div
                key={step.id || active}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: reduce ? 0 : 0.45, ease }}
                aria-live="polite"
              >
                <span className="au-j__count">
                  {pad(active + 1)} / {pad(n)}
                </span>
                <h3 className="au-j__year">{step.year_label}</h3>
                <h4 className="au-j__label">{step.label}</h4>
                {step.description && <p>{step.description}</p>}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* middle: the tree, the line grows to each step */}
          <div className="au-tree" role="list" aria-label="Journey steps">
            <span className="au-tree__rail" aria-hidden="true" />
            <motion.span className="au-tree__fill" aria-hidden="true" style={{ height: headTop }} />
            <motion.span className="au-tree__head" aria-hidden="true" style={{ top: headTop }} />
            {steps.map((s, i) => {
              const side = i % 2 === 0 ? "left" : "right";
              const cls = `au-tree__node au-tree__node--${side}${i === active ? " is-on" : ""}${i < active ? " is-past" : ""}`;
              return (
                <div key={s.id || i} className={cls} style={{ top: `${((i + 0.5) / n) * 100}%` }} role="listitem">
                  <span className="au-tree__branch" aria-hidden="true" />
                  <span className="au-tree__dot" aria-hidden="true" />
                  <button
                    type="button"
                    className="au-tree__chip"
                    aria-current={i === active ? "step" : undefined}
                    aria-label={`${s.year_label}: ${s.label}`}
                    onClick={() => goTo(i)}
                  >
                    <b>{s.year_label}</b>
                    <span>{s.label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* right: the globe turns to the place of the active step */}
          <div className="au-j__globe">
            <div className="au-globe">
              <Globe points={points} active={active} reduceMotion={!!reduce} />
              <span className="au-globe__ring" aria-hidden="true" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={active}
                  className="au-globe__tag"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {step.label}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


const Journey = ({ steps = [], photos = [] }) =>
  steps.length ? <JourneyStage key={steps.length} steps={steps} photos={photos} /> : null;

export default Journey;